import "./DataTable.css"
import React, {useMemo, useState} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import RootState from "../../../redux/states/root.state";
import { DataTableFoot, SortButton } from "./DataTableParts";

// The users holding a role or belonging to a user group (state.roleUsers serves both).
// Rendered inside a DataTable card whose toolbar the page owns, so this is just the
// scrolling table and its pager.

type Order = 'asc' | 'desc';
const headers = [
  {id:'id', label:'ID'},
  {id:'name', label:'User'},
  {id:'username', label:'Username'},
];

// Sorted on this page only (the API doesn't sort these lists)
const sortValue = (user: any, column: string) => (user?.[column] ?? '').toString().toLowerCase();

interface MembersTableProps {
  // Shown under "No Users Found"
  emptyHint?: string;
}

const MembersTable: React.FC<MembersTableProps> = ({emptyHint}) => {
  const navigate = useNavigate();
  const {data:users, loading:isLoading, meta} = useSelector((state: RootState) => state.roleUsers);
  const [searchParams, setSearchParams] = useSearchParams();

  // Table Sort
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState('');

  const rows = useMemo(() => {
    if (!orderBy) return users;
    const direction = order === 'asc' ? 1 : -1;
    return [...users].sort((a:any, b:any) => sortValue(a, orderBy).localeCompare(sortValue(b, orderBy), undefined, {numeric: true}) * direction);
  }, [users, orderBy, order]);

  const handleRequestSort = (header_id: string) => {
    const isAsc = orderBy === header_id && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header_id);
  };

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const totalPages = meta?.total_pages ?? meta?.last_page ?? 1;
  const total = meta?.total ?? 0;

  const goToPage = (value: number) => {
    searchParams.set("page", String(value));
    setSearchParams(searchParams);
  }

  return (
    <>
      <div className="dt-scroll">
        {isLoading ? (
          <div className="dt-state"><span className="spinner" role="progressbar" aria-label="Loading users"/></div>
        ) : users.length > 0 ? (
          <table className="dt-table">
            <thead>
              <tr>
                {headers.map((header)=>(
                  <th
                    key={header.id}
                    scope="col"
                    aria-sort={orderBy === header.id ? (order === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    <SortButton label={header.label} active={orderBy === header.id} order={order} onClick={()=>handleRequestSort(header.id)}/>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rows.map((user:any, i:number)=>(
                <tr key={user?.id ?? i}>
                  <td>
                    <a
                      href={`/user/${user.id}`}
                      className="dt-guid"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`View user ${user?.name ?? user?.id}`}
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                        e.preventDefault();
                        navigate(`/user/${user.id}`);
                      }}
                    >
                      {user?.id}
                    </a>
                  </td>
                  <td className="dt-strong">{user?.name || <span className="dt-na">—</span>}</td>
                  <td>{user?.username || <span className="dt-na">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="dt-state">
            <div className="dt-empty-title">No Users Found</div>
            {emptyHint && <span className="lp-muted">{emptyHint}</span>}
          </div>
        )}
      </div>

      <DataTableFoot
        shown={users?.length ?? 0}
        total={total}
        noun={total === 1 ? 'User' : 'Users'}
        page={page}
        totalPages={totalPages}
        loading={isLoading}
        onPage={goToPage}
      />
    </>
  );
};

export default MembersTable;
